import React, { FC } from "react";
import { BaseFeesWithCampaign } from "../../PriceList/BaseFeesWithCampaign/BaseFeesWithCampaign";
import { getDiscount } from "../Helpers/totalPriceCalculator";
import { formatPrice } from "../../Helpers/priceHelper";
import classNames from "classnames";
import { PriceListFees } from "../../PriceList/PriceListFees";
import { FeeWithDiscountTable } from "../../PriceList/FeeWithDiscountTable/FeeWithDiscountTable";
import { ServicePricingSummary } from "../../../Partials/PricingHighlight/ServicePricingSummary.csharp";
import { Divider } from "../Divider/Divider";

export const BookingPricingSummary: FC<ServicePricingSummary> = ({
  baseFees,
  listWithoutPrice,
  basePrice,
  eveningAddition,
  weekendHolidayAddition,
  isEvening,
  isWeekendOrHoliday,
  showWeekendEveningPrices,
  additionalFees,
  basePriceLabel,
  eveningPriceLabelInBooking,
  weekendPriceLabelInBooking,
  eveningPriceReservationLabel,
  endPriceLabel,
  additionalFeesLabel,
  isFriday,
  showWarningAboutHigherPrice,
  campaignPrice,
  highlightPriceRowOnHover,
  priceListFees,
}) => {
  const getEndPrice = () => {
    const baseFeesCost = baseFees.reduce(
      (prev, current) => prev + current.regularPrice,
      0
    );
    const price = getBasePrice();

    return formatPrice(
      price +
        baseFeesCost -
        getDiscount(isWeekendOrHoliday, isEvening, campaignPrice)
    );
  };

  const getBasePrice = () => {
    let price = basePrice;

    if (showWeekendEveningPrices) {
      if (isWeekendOrHoliday) price = basePrice + weekendHolidayAddition;
      else if (isEvening) price = basePrice + eveningAddition;
    }

    return price;
  };

  return (
    <>
      <div className="BookingPricingSummary">
        {!listWithoutPrice && (
          <>
            <table>
              <tbody>
                <tr
                  className={classNames("BookingPricingSummary__detailsRow", {
                    "BookingPricingSummary__detailsRow--highlightedOnHover": highlightPriceRowOnHover,
                  })}
                >
                  <th>
                    {campaignPrice?.basePriceLabel ?? basePriceLabel}
                  </th>
                  <td className="BookingPricingSummary__price">
                    {formatPrice(basePrice)}
                  </td>
                </tr>
                {showWeekendEveningPrices && (
                  <>
                    {isEvening && !!eveningAddition && (
                      <tr
                        className={classNames("BookingPricingSummary__detailsRow", {
                          "BookingPricingSummary__detailsRow--highlightedOnHover":
                            highlightPriceRowOnHover,
                        })}
                      >
                        <th>
                          {eveningPriceLabelInBooking ?? basePriceLabel}
                        </th>
                        <td className="BookingPricingSummary__price">
                          {formatPrice(eveningAddition)}
                        </td>
                      </tr>
                     )}
                    {isWeekendOrHoliday && !!weekendHolidayAddition && (
                      <tr
                        className={classNames("BookingPricingSummary__detailsRow", {
                          "BookingPricingSummary__detailsRow--highlightedOnHover":
                            highlightPriceRowOnHover,
                        })}
                      >
                        <th>
                          {weekendPriceLabelInBooking ?? basePriceLabel}
                        </th>
                        <td className="BookingPricingSummary__price">
                          {formatPrice(weekendHolidayAddition)}
                        </td>
                      </tr>
                    )}
                  </>
                )}
                <BaseFeesWithCampaign
                  baseFees={baseFees}
                  campaign={campaignPrice}
                  isWeekendOrHoliday={isWeekendOrHoliday}
                  isEvening={isEvening}
                />
              </tbody>
            </table>
            <Divider className="BookingPricingSummary__divider" />
            <div className="BookingPricingSummary__total">
              <span>{endPriceLabel}</span>
              <span className="BookingPricingSummary__price">{getEndPrice()}</span>
            </div>
          </>
        )}

        <PriceListFees
          priceListFees={priceListFees}
          highlightPriceRowOnHover={highlightPriceRowOnHover}
          campaignPrice={campaignPrice}
        />

        {(!!additionalFees.length || showWarningAboutHigherPrice) && (
          <>
            {(!!additionalFees.length ||
              weekendHolidayAddition > 0 ||
              eveningAddition > 0) && (
              <>
                <Divider className="BookingPricingSummary__divider" />
                <div className="BookingPricingSummary__additionalFeesLabel">{additionalFeesLabel}</div>
                <table>
                  <tbody>
                    {showWeekendEveningPrices &&
                      showWarningAboutHigherPrice &&
                      (isFriday && weekendHolidayAddition > 0 ? (
                        <tr
                          key={0}
                          className={classNames("BookingPricingSummary__detailsRow", {
                            "BookingPricingSummary__detailsRow--highlightedOnHover":
                              highlightPriceRowOnHover,
                          })}
                        >
                          <th>
                            {eveningPriceReservationLabel}
                          </th>
                          <td className="BookingPricingSummary__price">
                            {formatPrice(
                              weekendHolidayAddition -
                                (campaignPrice?.weekendHolidayAdditionDiscount ??
                                  0)
                            )}
                          </td>
                        </tr>
                      ) : (
                        eveningAddition > 0 && (
                          <tr
                            key={0}
                            className={classNames("BookingPricingSummary__detailsRow", {
                              "BookingPricingSummary__detailsRow--highlightedOnHover":
                                highlightPriceRowOnHover,
                            })}
                          >
                            <th>
                              {eveningPriceReservationLabel}
                            </th>
                            <td className="BookingPricingSummary__price">
                              {formatPrice(
                                eveningAddition -
                                  (campaignPrice?.eveningAdditionDiscount ?? 0)
                              )}
                            </td>
                          </tr>
                        )
                      ))}
                    <FeeWithDiscountTable
                      heading={additionalFeesLabel}
                      fees={additionalFees}
                      highlightPriceRowOnHover={highlightPriceRowOnHover}
                      getPrice={(fee) => fee.regularPrice}
                      getDiscount={(fee) => fee.regularDiscount}
                      showHeading={false}
                    />
                  </tbody>
                </table>
              </>
            )}
          </>
        )}
      </div>
    </>
  );
};
